import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getUserBrandAlerts as getUserBrandAlertsRequest,
  saveBrandAlert as saveBrandAlertRequest,
  deleteBrandAlertByProductId,
  updateBrandAlert
} from "./requests";
import { POSSIBLE_MODAL_ALERT_CONTENTS } from "constants/brands";
import { useAppContext } from "context/app-context";

export const useHandleBrandAlerts = () => {
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [currentContent, setCurrentContent] = useState(
    POSSIBLE_MODAL_ALERT_CONTENTS.FORM_ALERT
  );
  const [lastAlert, setLastAlert] = useState(null);
  const userBrandAlerts = useMemo(() => new Map(), []);
  const [productSourceId, setProductSourceId] = useState(null);
  const {
    sessionInfo: { isLoggedIn }
  } = useAppContext();

  const clearCurrentBrandAlert = useCallback(() => {
    userBrandAlerts.clear();
  }, [userBrandAlerts]);

  const getUserBrandAlerts = useCallback(async () => {
    try {
      const response = await getUserBrandAlertsRequest();
      response.forEach(({ brand_alert }) => {
        userBrandAlerts.set(brand_alert.product_source_id, brand_alert);
      });
    } catch (error) {
      clearCurrentBrandAlert();
    } finally {
      setIsInitialLoading(false);
    }
  }, [userBrandAlerts, clearCurrentBrandAlert]);

  const getCurrentBrandAlert = ({ productSourceId }) => {
    const brandAlertByProductSourceId = userBrandAlerts.get(
      Number(productSourceId)
    );
    if (!brandAlertByProductSourceId) return null;

    const { maximum_value, minimum_discount, minimum_value, frequency } =
      brandAlertByProductSourceId;
    return {
      maximumValue: Number(maximum_value),
      minimumValue: Number(minimum_value),
      minimumDiscount: minimum_discount,
      frequency
    };
  };

  const cancelAlert = async ({ productSourceId }) => {
    saveLastAlert({ productSourceId }); // saves the last alert momentarily in case the user wants to discard this deletion
    try {
      await deleteBrandAlertByProductId({ productSourceId });
      clearCurrentBrandAlert();
      await getUserBrandAlerts(); // refetch user brand alerts after cancel an alert
      setCurrentContent(POSSIBLE_MODAL_ALERT_CONTENTS.CANCEL_ALERT_FEEDBACK);
    } catch (error) {
      setLastAlert(null);
    }
  };

  const saveBrandAlert = async ({ formValues, productSourceId }) => {
    const hasCurrentBrand = !!getCurrentBrandAlert({ productSourceId });
    try {
      const request = hasCurrentBrand
        ? updateBrandAlert
        : saveBrandAlertRequest;
      await request({ ...formValues, productSourceId });
      await getUserBrandAlerts(); // refetch user brand alerts after saving an alert
      setCurrentContent(POSSIBLE_MODAL_ALERT_CONTENTS.SUCCESS_FEEDBACK);
    } catch (error) {
      throw new Error(
        "Could not possible to save your alert. Try again later."
      );
    }
  };

  const saveLastAlert = ({ productSourceId }) => {
    const brandAlert = getCurrentBrandAlert({ productSourceId });
    setLastAlert(brandAlert);
  };

  const resetAlert = async () => {
    await saveBrandAlert({ formValues: lastAlert, productSourceId });
  };

  useEffect(() => {
    if (!isLoggedIn) return;
    void getUserBrandAlerts();
  }, [getUserBrandAlerts, isLoggedIn]);

  return {
    isInitialLoading,
    saveBrandAlert,
    currentContent,
    setCurrentContent,
    cancelAlert,
    resetAlert,
    getCurrentBrandAlert,
    productSourceId,
    setProductSourceId
  };
};
